import { BodyMeasurementQuestion } from '@qmd-types/intakeForms';
import { PatientProfile } from '@qmd-types/patient';
import { z } from 'zod';

const MIN_FEET = 1;
const MAX_HEIGHT_INCHES = 9 * 12 + 11; // 9 feet 11 inches
const MIN_POUNDS = 1;
const MAX_POUNDS = 900;

function buildHeightFeetSchema(isRequired: boolean) {
  if (isRequired) {
    return z
      .string()
      .nonempty('Please enter your height')
      .transform((value) => parseInt(value, 10))
      .refine((value) => value >= MIN_FEET, {
        message: 'Please enter a valid height',
      });
  }

  return z
    .string()
    .optional()
    .transform((val) => (val ? parseInt(val) : undefined))
    .refine((value) => value === undefined || value >= MIN_FEET, {
      message: 'Please enter a valid height',
    });
}

function buildHeightInchesSchema(isRequired: boolean) {
  if (isRequired) {
    return z
      .string()
      .nonempty('Please enter your height in inches')
      .transform((value) => parseInt(value))
      .refine((value) => value >= 0 && value < 12, {
        message: 'Please enter a valid height in inches',
      });
  }

  return z
    .string()
    .optional()
    .transform((value) => (value ? parseInt(value) : undefined))
    .refine((value) => value === undefined || (value >= 0 && value < 12), {
      message: 'Please enter a valid height in inches',
    });
}

function buildWeightPoundsSchema(isRequired: boolean) {
  if (isRequired) {
    return z
      .string()
      .nonempty('Please enter your current weight')
      .transform((val) => parseInt(val, 10))
      .refine((val) => val >= MIN_POUNDS && val <= MAX_POUNDS, {
        message: 'Please enter a valid weight',
      });
  }

  return z
    .string()
    .optional()
    .transform((val) => (val ? parseInt(val) : undefined))
    .refine(
      (value) =>
        value === undefined || (value >= MIN_POUNDS && value <= MAX_POUNDS),
      {
        message: 'Please enter a valid weight',
      },
    );
}

export function buildSchema(isRequired: boolean) {
  const baseSchema = buildBaseSchema(isRequired);

  return baseSchema.superRefine((data, ctx) => {
    const totalHeightInches = getTotalHeightInches(data);

    if (!totalHeightInches) {
      return true;
    }

    if (totalHeightInches > MAX_HEIGHT_INCHES) {
      ctx.addIssue({
        path: ['heightFeet'],
        message: 'Please enter a valid height',
        code: z.ZodIssueCode.custom,
      });
      ctx.addIssue({
        path: ['heightInches'],
        code: z.ZodIssueCode.custom,
      });
    }
  });
}

function buildBaseSchema(isRequired: boolean) {
  return z.object({
    heightFeet: buildHeightFeetSchema(isRequired),
    heightInches: buildHeightInchesSchema(isRequired),
    weightPounds: buildWeightPoundsSchema(isRequired),
  });
}

export type FormValues = z.infer<ReturnType<typeof buildBaseSchema>>;

export function getDefaultFormValues(
  question: BodyMeasurementQuestion,
  patientProfile?: PatientProfile,
) {
  const heightInches = question.heightInches || patientProfile?.heightInches;
  const weightPounds = question.weightPounds || patientProfile?.weightPounds;

  return {
    heightFeet: heightInches ? Math.floor(heightInches / 12).toString() : '',
    heightInches: heightInches ? (heightInches % 12).toString() : '',
    weightPounds: weightPounds?.toString() || '',
  };
}

export function getTotalHeightInches(data: FormValues): number | undefined {
  const { heightFeet, heightInches } = data;

  if (!heightFeet && !heightInches) {
    return;
  }

  let totalHeightInches = heightFeet ? heightFeet * 12 : 0;

  if (heightInches) {
    totalHeightInches += heightInches;
  }

  return totalHeightInches;
}
